import type React from 'react';
import { useCallback, useRef } from 'react';

import { Button } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core-react/i18n';

import type { PhotoActionButtonsProps } from '../../types';

const FILE_INPUT_ID = 'endorsement-photo-input';

export const PhotoActionButtons: React.FC<PhotoActionButtonsProps> = ({ image, uppy }) => {
	const inputFileRef = useRef<HTMLInputElement>(null);

	const handleFileChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { files } = event.target;
			if (!files) return;

			const existingFiles = uppy.getFiles();
			existingFiles.forEach((existingFile) => {
				uppy.removeFile(existingFile.id);
			});

			Array.from(files).forEach((file) => {
				try {
					uppy.addFile({ source: 'react:FileInput', name: file.name, type: file.type, data: file });
				} catch {
					// errors handled in uppy abstraction
				}
			});

			if (inputFileRef.current) {
				inputFileRef.current.value = '';
			}
		},
		[uppy],
	);

	return (
		<div className="flex gap-2">
			<input
				ref={inputFileRef}
				type="file"
				onChange={handleFileChange}
				id={FILE_INPUT_ID}
				data-testid={FILE_INPUT_ID}
				data-qa={FILE_INPUT_ID}
				accept="image/*"
				className="sr-only"
			/>
			<Button
				{...({ as: 'label', htmlFor: FILE_INPUT_ID, role: '' } as unknown as EmptyProps)}
				variant="secondary-solid"
				size="small"
				data-testid="upload-photo-button"
				data-qa="upload-photo-button"
				className="cursor-pointer"
			>
				{image ? (
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.change_photo_button" />
				) : (
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.upload_photo_button" />
				)}
			</Button>
		</div>
	);
};
